// DAS-2207: Security Audit Modal — shows security findings with filtering/sorting
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Modal, Table, Tag, Select, Input, Space, Button, Statistic, Row, Col, Spin, Alert, Tooltip, message } from 'antd';
import { SearchOutlined, ReloadOutlined, CheckCircleOutlined, ExclamationCircleOutlined, FileAddOutlined } from '@ant-design/icons';
import api from '../functions/api';

const SEVERITY_COLOR = {
  CRITICAL: '#cf1322',
  HIGH: '#fa541c',
  MEDIUM: '#faad14',
  LOW: '#52c41a',
};

const SEVERITY_ORDER = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };

const CATEGORY_LABELS = {
  sql_injection: 'SQL Injection',
  endpoint: 'Endpoint',
  dependency: 'Dependency',
  secret: 'Secret',
  auth: 'Auth',
  headers: 'Headers',
};

const ALL_CATEGORIES = ['sql_injection', 'endpoint', 'dependency', 'secret', 'auth', 'headers'];

const SecurityAuditModal = ({ isVisible, onClose }) => {
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [severityFilter, setSeverityFilter] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [scanMode, setScanMode] = useState('full');
  const [diff, setDiff] = useState(null);
  const [scanRun, setScanRun] = useState(null);
  const [recheckingFp, setRecheckingFp] = useState(null);
  const [creatingJiraFp, setCreatingJiraFp] = useState(null);
  // DAS-3048: what the scan did NOT look at. Without this the finding count
  // reads as a verdict on the whole codebase, which it never was.
  const [coverage, setCoverage] = useState(null);
  const [coverageOpen, setCoverageOpen] = useState(false);

  // Load findings from database (no scan)
  const loadFromDb = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/security_audit/findings');
      setFindings(res.data.findings || []);
      setDiff(res.data.diff || null);
      setScanRun(res.data.scan_run || null);
      setCoverage(res.data.coverage || null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Run a new full/incremental scan
  const runScan = async () => {
    setScanning(true);
    setError(null);
    try {
      const res = await api.get(`/security_audit?mode=${scanMode}`);
      setFindings(res.data.findings || []);
      setDiff(res.data.diff || null);
      setCoverage(res.data.coverage || null);
      setScanRun({
        mode: res.data.mode,
        duration_seconds: res.data.duration_seconds,
        run_date: new Date().toISOString(),
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setScanning(false);
    }
  };

  // Recheck a single finding
  const recheckFinding = async (finding) => {
    setRecheckingFp(finding.fingerprint);
    try {
      const res = await api.post('/security_audit/recheck', {
        category: finding.category,
        fingerprint: finding.fingerprint,
      });
      if (res.data.still_present === false) {
        message.success(`Resolved: ${finding.title.substring(0, 60)}...`);
        setFindings(prev => prev.filter(f => f.fingerprint !== finding.fingerprint));
      } else {
        message.warning('Still present after recheck');
      }
    } catch (err) {
      message.error(err.response?.data?.error || err.message);
    } finally {
      setRecheckingFp(null);
    }
  };

  // Create Jira ticket for a finding
  const createJiraTicket = async (finding) => {
    setCreatingJiraFp(finding.fingerprint);
    try {
      // First get the formatted Jira body from our handler
      const bodyRes = await api.post('/security_audit/jira_body', { finding });
      const { summary, description, priority, labels } = bodyRes.data;

      // Then create the ticket via existing Jira endpoint
      const res = await api.post('/jira/create_ticket', {
        summary,
        description,
        issue_type: 'Bug',
        priority,
        labels,
      });

      if (res.data.key) {
        message.success(
          <span>
            Created <a href={res.data.url} target="_blank" rel="noopener noreferrer">{res.data.key}</a>
          </span>
        );
      }
    } catch (err) {
      message.error(err.response?.data?.error || err.message);
    } finally {
      setCreatingJiraFp(null);
    }
  };

  // On open: load from DB, never auto-scan
  useEffect(() => {
    if (isVisible) loadFromDb();
  }, [isVisible, loadFromDb]);

  const filtered = useMemo(() => {
    let data = [...findings];
    if (severityFilter.length) data = data.filter(f => severityFilter.includes(f.severity));
    if (categoryFilter.length) data = data.filter(f => categoryFilter.includes(f.category));
    if (searchText) {
      const s = searchText.toLowerCase();
      data = data.filter(f =>
        f.title.toLowerCase().includes(s) ||
        f.file.toLowerCase().includes(s) ||
        (f.recommendation || '').toLowerCase().includes(s) ||
        (f.owasp || '').toLowerCase().includes(s) ||
        (f.cwe || '').toLowerCase().includes(s) ||
        (f.code_snippet || '').toLowerCase().includes(s) ||
        (f.function_name || '').toLowerCase().includes(s)
      );
    }
    return data;
  }, [findings, severityFilter, categoryFilter, searchText]);

  const stats = useMemo(() => {
    const bySev = {};
    const byCat = {};
    findings.forEach(f => {
      bySev[f.severity] = (bySev[f.severity] || 0) + 1;
      byCat[f.category] = (byCat[f.category] || 0) + 1;
    });
    return { bySev, byCat };
  }, [findings]);

  const columns = [
    {
      title: 'Severity',
      dataIndex: 'severity',
      width: 90,
      sorter: (a, b) => (SEVERITY_ORDER[a.severity] ?? 4) - (SEVERITY_ORDER[b.severity] ?? 4),
      defaultSortOrder: 'ascend',
      render: (sev, rec) => (
        <Tooltip title={rec.severity_reason || sev}>
          <Tag color={SEVERITY_COLOR[sev]}>{sev}</Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      width: 110,
      sorter: (a, b) => a.category.localeCompare(b.category),
      render: cat => CATEGORY_LABELS[cat] || cat,
    },
    {
      title: 'Finding',
      dataIndex: 'title',
      sorter: (a, b) => a.title.localeCompare(b.title),
      render: (title, rec) => {
        const isRechecking = recheckingFp === rec.fingerprint;
        const isCreatingJira = creatingJiraFp === rec.fingerprint;
        const recText = rec.recommendation
          ? rec.recommendation.replace(/Chain: /, '\nChain: ')
          : '';

        return (
          <div>
            <div style={{ fontWeight: 500 }}>{title}</div>
            <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>
              <code style={{ background: '#f0f0f0', padding: '0 4px', borderRadius: 2 }}>
                {rec.file}{rec.line ? `:${rec.line}` : ''}
              </code>
              {rec.function_name && (
                <span style={{ marginLeft: 8 }}>
                  in <code style={{ background: '#f0f0f0', padding: '0 4px', borderRadius: 2 }}>{rec.function_name}</code>
                </span>
              )}
            </div>
            {rec.code_snippet && (
              <pre style={{
                background: '#f8f8f8', padding: '4px 8px', borderRadius: 3,
                fontSize: 11, margin: '4px 0 2px', overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%',
                color: '#c41d7f', border: '1px solid #f0f0f0',
              }}>{rec.code_snippet}</pre>
            )}
            <div style={{ fontSize: 11, color: '#888', marginTop: 2, whiteSpace: 'pre-line' }}>
              {recText}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <Tooltip title="Create Jira ticket with all finding details">
                <Button
                  size="small"
                  type="primary"
                  loading={isCreatingJira}
                  onClick={() => createJiraTicket(rec)}
                  icon={<FileAddOutlined />}
                  style={{ fontSize: 11 }}
                >
                  Jira ticket
                </Button>
              </Tooltip>
              <Tooltip title="Re-run analyzer to check if still present">
                <Button
                  size="small"
                  type="link"
                  loading={isRechecking}
                  onClick={() => recheckFinding(rec)}
                  icon={<CheckCircleOutlined />}
                  style={{ fontSize: 11, padding: '0 4px' }}
                >
                  Verify
                </Button>
              </Tooltip>
            </div>
          </div>
        );
      },
    },
    {
      title: 'OWASP',
      dataIndex: 'owasp',
      width: 120,
      ellipsis: true,
      render: val => val ? <span style={{ fontSize: 11 }}>{val}</span> : '—',
    },
    {
      title: 'CWE',
      dataIndex: 'cwe',
      width: 80,
      render: val => {
        if (!val) return '—';
        const id = val.replace(/\D/g, '');
        return (
          <a href={`https://cwe.mitre.org/data/definitions/${id}.html`}
             target="_blank" rel="noopener noreferrer" style={{ fontSize: 11 }}>{val}</a>
        );
      },
    },
  ];

  const severityOptions = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(s => ({
    label: `${s} (${stats.bySev[s] || 0})`, value: s,
  }));
  const categoryOptions = ALL_CATEGORIES.map(c => ({
    label: `${CATEGORY_LABELS[c] || c} (${stats.byCat[c] || 0})`,
    value: c,
  }));

  return (
    <Modal
      open={isVisible}
      onCancel={onClose}
      footer={null}
      width="95%"
      title="Security Audit — DAS-2207"
      destroyOnClose
    >
      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col><Statistic title="Total" value={findings.length} /></Col>
        {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(sev => (
          <Col key={sev}>
            <Statistic
              title={sev}
              value={stats.bySev[sev] || 0}
              valueStyle={{ color: SEVERITY_COLOR[sev] }}
            />
          </Col>
        ))}
        {scanRun && (
          <>
            {scanRun.duration_seconds != null && (
              <Col><Statistic title="Scan time" value={`${scanRun.duration_seconds}s`} /></Col>
            )}
            <Col>
              <div style={{ fontSize: 11, color: '#888', marginTop: 8 }}>
                Last scan: {scanRun.run_date ? new Date(scanRun.run_date).toLocaleString() : '—'}
                {scanRun.mode && ` (${scanRun.mode})`}
              </div>
            </Col>
          </>
        )}
      </Row>

      {/* DAS-3048: say what was not examined, next to the number that invites
          the reader to assume everything was. */}
      {coverage && (
        <Alert
          type={coverage.unparsed_files?.length ? 'warning' : 'info'}
          showIcon
          data-testid="security-audit-coverage"
          style={{ marginBottom: 16 }}
          message={
            <span>
              This count covers {coverage.examined?.length || 0} kinds of check.
              {' '}
              <strong>{coverage.not_examined?.length || 0} things were not examined</strong>
              {coverage.unparsed_files?.length
                ? ` — and ${coverage.unparsed_files.length} file(s) could not be read at all.`
                : '.'}
              {' '}
              <Button
                type="link"
                size="small"
                style={{ padding: 0 }}
                data-testid="security-audit-coverage-toggle"
                onClick={() => setCoverageOpen(o => !o)}
              >
                {coverageOpen ? 'Hide the scope' : 'What was not examined?'}
              </Button>
            </span>
          }
          description={coverageOpen && (
            <div data-testid="security-audit-coverage-detail" style={{ fontSize: 12 }}>
              <div style={{ marginTop: 4 }}><strong>Examined</strong></div>
              <ul style={{ margin: '4px 0 8px 18px' }}>
                {(coverage.examined || []).map((item, i) => <li key={`e${i}`}>{item}</li>)}
              </ul>
              <div><strong>Not examined — a clean result here proves nothing</strong></div>
              <ul style={{ margin: '4px 0 8px 18px' }}>
                {(coverage.not_examined || []).map((item, i) => <li key={`n${i}`}>{item}</li>)}
              </ul>
              {coverage.unparsed_files?.length > 0 && (
                <>
                  <div><strong>Could not be parsed, so not examined</strong></div>
                  <ul style={{ margin: '4px 0 0 18px' }}>
                    {coverage.unparsed_files.map((item, i) => <li key={`u${i}`}>{item}</li>)}
                  </ul>
                </>
              )}
            </div>
          )}
        />
      )}

      {diff && (diff.new > 0 || diff.resolved > 0) && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            <span>
              Since last scan:
              {diff.new > 0 && <Tag color="green" style={{ marginLeft: 8 }}>{diff.new} new</Tag>}
              {diff.resolved > 0 && <Tag color="blue" style={{ marginLeft: 4 }}>{diff.resolved} resolved</Tag>}
              {diff.existing > 0 && <span style={{ marginLeft: 8, color: '#888' }}>{diff.existing} unchanged</span>}
            </span>
          }
        />
      )}

      {!loading && !scanning && findings.length === 0 && !error && (
        <Alert
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined />}
          style={{ marginBottom: 16 }}
          message="No findings in database. Run a full scan to populate."
        />
      )}

      <Space wrap style={{ marginBottom: 16 }}>
        <Select
          mode="multiple"
          allowClear
          placeholder="Filter severity"
          options={severityOptions}
          value={severityFilter}
          onChange={val => setSeverityFilter(val || [])}
          style={{ minWidth: 200 }}
          getPopupContainer={trigger => trigger.parentNode}
          maxTagCount="responsive"
        />
        <Select
          mode="multiple"
          allowClear
          placeholder="Filter category"
          options={categoryOptions}
          value={categoryFilter}
          onChange={val => setCategoryFilter(val || [])}
          style={{ minWidth: 220 }}
          getPopupContainer={trigger => trigger.parentNode}
          maxTagCount="responsive"
        />
        <Input
          placeholder="Search title, file, code, function, OWASP, CWE..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          allowClear
          style={{ width: 340 }}
        />
        <Select
          value={scanMode}
          onChange={setScanMode}
          style={{ width: 140 }}
          options={[
            { label: 'Full scan', value: 'full' },
            { label: 'Incremental', value: 'incremental' },
          ]}
          getPopupContainer={trigger => trigger.parentNode}
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={runScan}
          loading={scanning}
          type="primary"
          danger
        >
          {scanning ? 'Scanning...' : 'Run scan'}
        </Button>
      </Space>

      <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
        Showing {filtered.length} of {findings.length} findings
      </div>

      <Spin spinning={loading || scanning}>
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey={(rec, idx) => rec.fingerprint || idx}
          size="small"
          pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['25', '50', '100', '200'] }}
          scroll={{ y: 600 }}
        />
      </Spin>
    </Modal>
  );
};

export default SecurityAuditModal;
